/*
 * Narrava — Mood Detection
 * ─────────────────────────────────────────────────────────────────────────
 * KEYWORD DICTIONARY (edit freely — case-insensitive, stem-based match)
 *
 *   grief    →  grief, grieving, loss, lost (death), died, death,
 *               miss, missing, hurt, pain, heartbreak, mourn, weep, sorrow,
 *               broken, abandon, alone, lonely
 *
 *   fear     →  anxious, anxiety, anxieties, afraid, fear, scared, panic,
 *               overwhelm, overwhelmed, worry, worried, nervous, dread,
 *               terrified, stress, stressed
 *
 *   purpose  →  lost (direction), purpose, direction, confused, stuck,
 *               meaning, meaningless, drift, drifting, path, dharma,
 *               calling, career, future
 *
 *   hope     →  hope, hopeful, grateful, gratitude, thankful, joy, joyful,
 *               happy, love, loved, peace, blessed, trust, faith
 *
 *   anger    →  angry, anger, furious, fury, hate, rage, frustrated,
 *               frustrating, resent, resentment, betray, betrayed
 *
 * Edge case: if a message has keywords from multiple buckets, the LATER
 * emotion in the sentence wins (the seeker's current trajectory matters).
 * If a single keyword appears in two buckets (e.g., "lost"), the later
 * unambiguous keyword decides.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const MOOD_KEYWORDS = {
  grief: [
    "grief", "grieving", "died", "death", "miss", "missing",
    "hurt", "pain", "heartbreak", "mourn", "weep", "sorrow",
    "broken", "abandon", "alone", "lonely",
  ],
  fear: [
    "anxious", "anxiety", "afraid", "fear", "scared", "panic",
    "overwhelm", "worry", "worried", "nervous", "dread",
    "terrified", "stress", "stressed",
  ],
  purpose: [
    "purpose", "direction", "confused", "stuck", "meaning",
    "meaningless", "drift", "path", "dharma", "calling",
    "career", "future",
  ],
  hope: [
    "hope", "hopeful", "grateful", "gratitude", "thankful", "joy",
    "joyful", "happy", "love", "loved", "peace", "blessed",
    "trust", "faith",
  ],
  anger: [
    "angry", "anger", "furious", "fury", "hate", "rage",
    "frustrated", "frustrating", "resent", "resentment",
    "betray", "betrayed",
  ],
};

// Ambiguous keywords that alone shouldn't force a switch — only counted if
// they appear alongside a clearer keyword.
const AMBIGUOUS = new Set(["lost"]);

/**
 * Detect the dominant mood in a user message.
 * Returns one of: "grief" | "fear" | "purpose" | "hope" | "anger" | null
 *
 * Strategy:
 *  - Lowercase + split into words.
 *  - For each word, check each bucket with stem match (word.includes(stem)).
 *  - Track [index, bucket] hits.
 *  - Return the bucket of the LAST unambiguous hit.
 *  - Return null if ambiguous/no match (caller keeps current scene).
 */
export function detectMood(text) {
  if (!text || typeof text !== "string") return null;
  const lower = text.toLowerCase();
  const tokens = lower.split(/[^a-z']+/).filter(Boolean);

  const hits = [];
  tokens.forEach((tok, i) => {
    if (AMBIGUOUS.has(tok)) return;
    for (const [bucket, words] of Object.entries(MOOD_KEYWORDS)) {
      for (const w of words) {
        // stem match: token includes keyword OR keyword includes token (for variants)
        if (tok === w || tok.includes(w) || (w.length >= 5 && w.includes(tok))) {
          hits.push({ i, bucket });
          return; // one bucket per token
        }
      }
    }
  });

  if (hits.length === 0) return null;
  return hits[hits.length - 1].bucket; // later wins
}
